import { useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw } from 'lucide-react';

import { applyLayout, bandsOf, PINNED, unmanagedBands } from './draftLayout';
import SectionOrderList from './SectionOrderList';

export default function DraftLayout({
  html,
  onChange,
  onSave,
  saving,
}: {
  html: string;
  onChange: (html: string | null) => void;
  onSave: (html: string) => void;
  saving: boolean;
}) {
  const initial = useMemo(() => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const bands = bandsOf(doc);
    return {
      order: bands.map((el) => el.id),
      hidden: new Set(bands.filter((el) => el.hidden).map((el) => el.id)),
      unmanaged: unmanagedBands(doc),
    };
  }, [html]);

  const [order, setOrder] = useState(initial.order);
  const [hidden, setHidden] = useState(initial.hidden);

  useEffect(() => {
    setOrder(initial.order);
    setHidden(initial.hidden);
  }, [initial]);

  const dirty =
    order.join() !== initial.order.join() ||
    [...hidden].sort().join() !== [...initial.hidden].sort().join();

  useEffect(() => {
    onChange(dirty ? applyLayout(html, order, hidden) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, hidden, dirty, html]);

  const movable = order.filter((id) => !PINNED.has(id));
  const move = (from: number, to: number) => {
    if (to < 0 || to >= movable.length) return;
    const next = [...movable];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOrder([
      ...(order.includes('header') ? ['header'] : []),
      ...next,
      ...(order.includes('footer') ? ['footer'] : []),
    ]);
  };
  const toggle = (id: string) =>
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (!movable.length && !initial.unmanaged.length) return null;

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Sections on this draft</h2>
        {dirty && (
          <button
            type="button"
            onClick={() => {
              setOrder(initial.order);
              setHidden(initial.hidden);
            }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Undo
          </button>
        )}
      </div>
      {movable.length > 0 && (
        <>
          <p className="mb-1 mt-1 text-xs text-muted-foreground">
            Reorder or switch off bands without generating again. Header and footer stay put.
          </p>
          <SectionOrderList order={movable} onMove={move} hidden={hidden} onToggleHidden={toggle} />
        </>
      )}
      {initial.unmanaged.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          These bands can't be moved or switched off here, because of where they sit in the page:{' '}
          {initial.unmanaged.join(', ')}.
        </p>
      )}
      {movable.length > 0 && (
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => onSave(applyLayout(html, order, hidden))}
          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-coral px-4 py-2 text-sm
                     font-medium text-white hover:bg-coral-hover disabled:opacity-50"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save layout
        </button>
      )}
    </section>
  );
}
